<template>
	<div>
		<NuxtLayout name="page">
			<h1>Kontakt</h1>
			<p>
				Wir freuen uns über Ihr Interesse an unserem Unternehmen. Wenn Sie Fragen oder Anregungen haben, zögern Sie bitte nicht, uns zu kontaktieren. Wir sind gerne für Sie da und helfen Ihnen weiter.
			</p>
			<div class="alert success" v-if="alertSuccess">
				{{ alertSuccess }}
			</div>
			<div class="alert error" v-if="alertError">
				{{ alertError }}
			</div>
			<NuxtErrorBoundary>
				<form @submit.prevent="submitForm">
					<div>
						<label for="name">Name</label>
						<input type="text" v-model="name" id="name" maxlength="75" required>
					</div>
					<div>
						<label for="email">E-Mail</label>
						<input type="email" v-model="email" id="email" maxlength="100" required>
						<small class="text-muted">Wir verwenden Ihre E-Mail-Adresse nur für die Bearbeitung Ihrer Anfrage.</small>
					</div>
					<div>
						<label for="subject">Betreff</label>
						<input type="text" v-model="subject" id="subject" maxlength="100" required>
					</div>
					<div>
						<label for="message">Nachricht</label>
						<textarea ref="messageRef" rows="5" id="message" v-model="message" maxlength="2000" required></textarea>
					</div>
					<div>
						<span class="checkbox">
							<input type="checkbox" id="privacy" v-model="privacy" required>
							<i></i>
						</span>
						<label for="privacy">Ich habe die <NuxtLink to="/privacy/" aria-label="Erfahren Sie mehr über unsere Datenschutzrichtlinien">Datenschutzerklärung</NuxtLink> gelesen und akzeptiere diese.</label>
					</div>
					<div class="buttons">
						<button type="submit" role="button" :disabled="submitDisabled">Absenden</button>
					</div>
				</form>
			</NuxtErrorBoundary>
		</NuxtLayout>
	</div>
</template>

<script setup lang="ts">
import { getAnalytics, logEvent } from '@firebase/analytics'
import { useTextareaAutosize } from '@vueuse/core'
import { initializeApp } from 'firebase/app'
import type IContactRequest from '~~/shared/types/IContactRequest'
import { validateEmail, validateMessage, validateName, validateSubject } from '~~/shared/utils/validation'

declare global {
	interface Window {
		grecaptcha: {
			ready: (callback: () => void) => void
			execute: (siteKey: string, options: { action: string }) => Promise<string>
		}
	}
}

useSeoMeta({
	title: 'Kontakt',
	ogTitle: 'Kontakt: Mailuft',
	twitterTitle: 'Kontakt: Mailuft',
	ogDescription: 'Wir sind für Sie da. Nehmen Sie Kontakt mit unserem Expertenteam auf, um individuelle Beratung oder technischen Support zu erhalten. Wir freuen uns auf Ihre Nachricht!',
	description: 'Wir sind für Sie da. Nehmen Sie Kontakt mit unserem Expertenteam auf, um individuelle Beratung oder technischen Support zu erhalten. Wir freuen uns auf Ihre Nachricht!',
	twitterDescription: 'Wir sind für Sie da. Nehmen Sie Kontakt mit unserem Expertenteam auf, um individuelle Beratung oder technischen Support zu erhalten. Wir freuen uns auf Ihre Nachricht!',
})

const config = useRuntimeConfig()
const recaptchaLoaded = ref(false)
const alertSuccess = ref('')
const alertError = ref('')
const submitDisabled = ref(false)
const name = ref('')
const email = ref('')
const subject = ref('')
const { textarea: messageRef, input: message } = useTextareaAutosize({styleProp: 'minHeight'})
const privacy = ref(false)

definePageMeta({
	layout: 'page',
})

onErrorCaptured((err: any) =>
{
	if( err.response && err.response._data )
	{
		alertError.value = err.response._data.message
	}
	else
	{
		alertError.value = err.message
	}

	submitDisabled.value = false
})

function loadReCaptcha()
{
	return new Promise((res, rej) =>
	{
		useHead({
			script: [
				{
					src: `https://www.google.com/recaptcha/api.js?render=${config.public.apis.recaptcha}`,
					async: true,
					onload: res,
					onerror: rej,
				},
			],
		})		
	})
}

function getRecaptchaToken( action: string )
{
	return new Promise((resolve, reject) =>
	{
		const grecaptcha = import.meta.client ? window.grecaptcha : null
		if( !grecaptcha || !grecaptcha.ready )
		{
			return reject(new Error('reCAPTCHA Skript nicht geladen.'))
		}

		grecaptcha.ready(() => {
		grecaptcha.execute(config.public.apis.recaptcha, { action })
			.then(resolve)
			.catch(reject)
		})
	})
}

async function submitForm()
{
	alertSuccess.value = ''
	alertError.value = ''
	submitDisabled.value = true

	if( !recaptchaLoaded.value )
	{
		await loadReCaptcha()
		recaptchaLoaded.value = true
	}
	
	const captchaToken = await getRecaptchaToken('contact')

	if( !validateName( name.value ) )
	{
		throw new Error('Sie müssen einen Namen angeben (maximal 75 Zeichen).')
	}

	if( !validateEmail( email.value ) )
	{
		throw new Error('Sie müssen eine gültige E-Mail Adresse angeben.')
	}

	if( !validateSubject( subject.value ) )
	{
		throw new Error('Sie müssen einen Betreff angeben (maximal 100 Zeichen).')
	}

	if( !validateMessage( message.value ) )
	{
		throw new Error('Sie müssen eine Nachricht angeben (maximal 2.000 Zeichen).')
	}

	if( !privacy.value )
	{
		throw new Error('Sie müssen unsere Datenschutzerklärung akzeptieren.')
	}

	if( !captchaToken )
	{
		throw new Error('Es konnte kein Captcha-Token erstellt werden. Bitte versuchen Sie es später erneut.')
	}

	const resp = await $fetch('/api/contact',
	{
		cache: 'no-cache',
		method: 'POST',
		body: {
			name: name.value,
			email: email.value,
			subject: subject.value,
			message: message.value,
			privacy: privacy.value,
			token: captchaToken,
		} as IContactRequest,
	})

	if( resp.success )
	{
		alertSuccess.value = 'Ihre Nachricht wurde erfolgreich versendet. Wir setzen uns schnellstmöglich mit Ihnen in Verbindung.'

		useNuxtApp().$log( 'contact_form', { name: name.value, email: email.value, } )

		clearForm()
	}
	else
	{
		alertError.value = 'Ihre Nachricht konnte nicht an uns versendet werden. Versuchen Sie es später erneut.'
	}

	submitDisabled.value = false
}

function clearForm()
{
	name.value = ''
	email.value = ''
	subject.value = ''
	message.value = ''
	privacy.value = false
}
</script>

<style lang="css" scoped>
form
{
	display: flex;
	gap: 1.5rem;
	flex-direction: column;
	margin: 0;
	padding: 0;
}

form > div
{
	flex: 1;
}

form label
{
	display: block;
	margin-bottom: 0.25rem;
	font-family: "brother-1816", sans-serif;
	color: rgb(var(--blue));
	font-size: .9rem;
	text-transform: uppercase;
	letter-spacing: 2px;
}

form input:not([type="checkbox"], [type="radio"]),
form textarea
{
	display: block;
	width: 100%;
	background: #fff;
	box-sizing: border-box;
	border: none;
	padding: .75rem 1rem;
	border-radius: .35rem;
	font-size: 1.2rem;
	outline: none;
	box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 2px 0px;
	transition: box-shadow .2s ease;
}

form input:not([type="checkbox"], [type="radio"]):focus,
form textarea:focus
{
	box-shadow: 0 0 0 4px rgba(var(--blue-light),.4);
}

form textarea
{
	resize: none;
}

form .checkbox,
form .radio
{
	position: relative;
	display: inline-block;
	background: #fff;
	border: none;
	box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 2px 0px;
	border-radius: .2rem;
	width: 18px;
	height: 18px;
	vertical-align: middle;
	transition: box-shadow .2s ease;
	cursor: pointer;
}

form .checkbox:focus-within,
form .radio:focus-within
{
	box-shadow: 0 0 0 4px rgba(var(--blue-light),.4);
}

form .checkbox input[type="checkbox"],
form .radio input[type="radio"]
{
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	opacity: 0;
	margin: 0;
	z-index: 2;
}

form .checkbox i
{
	display: block;
	position: absolute;
	width: 4px;
	height: 7px;
	border-right: 2px solid rgb(var(--blue));
	border-bottom: 2px solid rgb(var(--blue));
	top: 3px;
	left: 6px;
	transform: rotate(45deg);
	opacity: 0;
	transition: opacity .25s ease;
	z-index: 1;
}

form .checkbox input:checked + i,
form .radio input:checked + i
{
	opacity: 1;
}

form .checkbox + label,
form .radio + label
{
	display: inline;
	font-size: 1rem;
	color: rgb(var(--black));
	vertical-align: middle;
	padding: 0 0 0 .6rem;
	text-transform: none;
	letter-spacing: normal;
}

form .buttons
{
	margin-top: 1rem;
}

form button
{
	display: block;
	width: 100%;
	background: rgb(var(--blue));
	box-sizing: border-box;
	border: none;
	padding: .75rem 1rem;
	border-radius: .35rem;
	letter-spacing: 2px;
	font-weight: 500;
	font-size: 1.2rem;
	text-transform: uppercase;
	outline: none;
	color: #fff;
	cursor: pointer;
	transition: background, .2s ease;
}

form button:hover
{
	background: rgb(var(--blue-light));
}

form button[disabled]
{
	opacity: .7;
	pointer-events: none;
}
</style>